'use client';
import { useMemo } from 'react';

import type { CancelTokenSource } from 'axios';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { create } from 'zustand';

import {
    EMAIL_LOCAL_STORAGE_KEY,
    OAUTH_PREFILL_FIRST_NAME,
    OAUTH_PREFILL_LAST_NAME,
    PHONE_LOCAL_STORAGE_KEY,
    USER_ID_LOCAL_STORAGE_KEY,
    USER_ROLE_LOCAL_STORAGE_KEY,
} from 'constants/session';
import type { FacebookPixelTrackerPayload } from 'lib/facebookPixel';
import {
    resolveAccountPolicyAvailability,
    selectPerpsOnlyStatus,
    type AccountPolicyAvailability,
} from 'lib/perpsOnlyStatus';
import { runAppLogoutTeardown } from 'lib/stores/appLogoutTeardown';
import type { ServerError, UserVo } from 'models';
import type { CreateMarketRequestRequest, LoginResponse, UpdateUserRequest } from 'models/reqres';
import type { AccessLevel } from 'models/user';
import { getCountryCodeSnapshot } from 'queries/countryCode';
import { clearNotificationFlushPending } from 'queries/notificationPreferences';
import { ensureSsnStatusLoaded } from 'queries/ssn';
import { ensureUserPropertiesLoaded, getUserPropertiesSnapshot, updateUserProperties } from 'queries/userProperties';
import { useAttributionStore } from 'stores/zustand/attributionStore';
import { useReferralStore } from 'stores/zustand/referralStore';
import { displayApplicationIncomplete, useRegistrationStore } from 'stores/zustand/registrationStore';
import { handleSnackbarError, useSnackbarStore } from 'stores/zustand/snackbarStore';
import { useTwoFactorAuthStore } from 'stores/zustand/twoFactorAuthStore';
import { clearCsrfToken } from 'transport/core';
import {
    deactivateAccount as deactivateAccountTransport,
    endFESession,
    logout as logoutTransport,
    oauthLogin,
    oauthTwoFactorLogin,
    startFESession as startFESessionTransport,
    twoFactorLogin as twoFactorLoginTransport,
} from 'transport/domains/auth';
import { createMarketRequest as createMarketRequestTransport } from 'transport/domains/portfolio';
import {
    getProfile,
    requestUpdateUserEmail as requestUpdateUserEmailTransport,
    resendAccountVerification,
    updateUserEmail as updateUserEmailTransport,
    updateUserInfo,
    verifyAccount as verifyAccountTransport,
    verifyPendingEmail as verifyPendingEmailTransport,
} from 'transport/domains/profile';
import { sendResetPasswordLink, verifyPasswordReset } from 'transport/domains/signup';
import { legacyExchangeWsClient } from 'transport/wsClients';
import analyticsProvider from 'utils/analytics/analyticsProvider';
import { getErrorMessage } from 'utils/errors';
import { clearFrontendSessionId, getValidFrontendSessionId, storeFrontendSessionId } from 'utils/frontendSession';
import { GrowthEvent, growthEventId } from 'utils/growthEventId';
import { submitStoredReferralAfterLogin } from 'utils/loginReferral';
import { OAuthProvider } from 'utils/oauth';
import { resolveEligibleReferralForSubmission } from 'utils/referralAttribution';
import { fetchSardineSessionKey, resetSardineState } from 'utils/sardine';
import { setSprigAttribute } from 'utils/sprig';
import { removeCookie, setCookieWithExpiration, setLocalStorageWithExpiration } from 'utils/storage';
import { uuidv4 } from 'utils/uuid';

import { useAuthStore } from './authStore';
import { resetUserOrdersRefreshState, subscribeUserOrdersWs, unsubscribeUserOrdersWs } from './userOrdersStore';

const noop = (): null => null;

export enum LoginType {
    OAUTH = 'oauth',
    EMAIL = 'email',
}

declare const window: any;

interface NavigatorWithUserAgentData extends Navigator {
    userAgentData?: {
        mobile?: boolean;
    };
}

function isMobileWebDevice(): boolean {
    if (typeof navigator === 'undefined') return false;

    const userAgentData = (navigator as NavigatorWithUserAgentData).userAgentData;
    if (userAgentData?.mobile) return true;

    const userAgent = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    return (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(userAgent) ||
        (platform === 'MacIntel' && maxTouchPoints > 1)
    );
}

// ---------------------------------------------------------------------------
// Reactive session flags (`loading`/`verifying`). `verified`/`first_time` were
// write-only/never-read dead fields on the old UserStore — dropped during the
// port, not carried forward.
// ---------------------------------------------------------------------------

export type UserProfileLoadState =
    | { phase: 'idle'; userId: null; error: null }
    | { phase: 'loading'; userId: UserVo['user_id']; error: null }
    | { phase: 'ready'; userId: UserVo['user_id']; error: null }
    | { phase: 'error'; userId: UserVo['user_id']; error: ServerError };

interface UserSessionState {
    loading: boolean;
    verifying: boolean;
    profileLoadState: UserProfileLoadState;
}

export const useUserSessionStore = create<UserSessionState>()(() => ({
    loading: false,
    verifying: false,
    profileLoadState: { phase: 'idle', userId: null, error: null },
}));

/**
 * Reactive account-policy lifecycle for policy-gated UI. Request-time checks
 * continue to use selectPerpsOnlyStatus directly so loading and errors fail
 * closed as `unknown`.
 */
export function useAccountPolicyAvailability(): AccountPolicyAvailability {
    const policyStatus = useAuthStore(selectPerpsOnlyStatus);
    const userId = useAuthStore((state) => state.userId);
    const profileLoadState = useUserSessionStore((state) => state.profileLoadState);

    return useMemo(
        () => resolveAccountPolicyAvailability(policyStatus, userId, profileLoadState),
        [policyStatus, profileLoadState, userId],
    );
}

type ActiveUserSessionLoad = {
    userId: string;
    promise: Promise<UserVo | null>;
};

let activeUserSessionLoad: ActiveUserSessionLoad | null = null;
let userProfileEpoch = 0;

/** Fetches the user's profile and bootstraps the session (SSN status, FE session, WS connect, properties cache). */
export function loadUserSession(onErr: (err: ServerError) => void = noop): Promise<UserVo | null> {
    const userId = useAuthStore.getState().userId ?? '';
    const profileEpoch = userProfileEpoch;
    if (!userId) {
        return Promise.resolve(null);
    }
    if (activeUserSessionLoad?.userId === userId) {
        return activeUserSessionLoad.promise;
    }

    useUserSessionStore.setState({
        loading: true,
        profileLoadState: { phase: 'loading', userId, error: null },
    });

    let resolveLoad: (user: UserVo | null) => void = noop;
    const loadPromise = new Promise<UserVo | null>((resolve) => {
        resolveLoad = resolve;
    });
    activeUserSessionLoad = { userId, promise: loadPromise };

    const finish = (user: UserVo | null): void => {
        if (activeUserSessionLoad?.promise === loadPromise) {
            useUserSessionStore.setState({ loading: false });
        }
        resolveLoad(user);
    };

    getProfile(
        userId,
        (user) => {
            // A response from a session that ended or switched users must not
            // repopulate the new session with the old account's policy.
            if (useAuthStore.getState().userId !== userId || profileEpoch !== userProfileEpoch) {
                finish(null);
                return;
            }

            useAuthStore.getState().actions.userUpdated(user);
            useUserSessionStore.setState({
                profileLoadState: { phase: 'ready', userId, error: null },
            });
            void ensureSsnStatusLoaded(userId);
            const feSessionID = getValidFrontendSessionId();
            const isLoggedInMember = useAuthStore.getState().isLoggedInMember;

            if (isLoggedInMember) {
                if (!feSessionID) {
                    startFESessionTransport(
                        userId,
                        (response) => {
                            storeFrontendSessionId(response.session_id);
                        },
                        noop,
                        (): void => {
                            legacyExchangeWsClient.connect();
                        },
                    );
                } else {
                    legacyExchangeWsClient.connect();
                }
            }

            subscribeUserOrdersWs();

            // Only clear `loading` after the properties cache is populated so the
            // color palette is set before the UI renders (important for dark mode).
            void ensureUserPropertiesLoaded().then(
                () => finish(user),
                () => finish(user),
            );
        },
        (err) => {
            if (useAuthStore.getState().userId !== userId || profileEpoch !== userProfileEpoch) {
                finish(null);
                return;
            }

            // This means that the userId we had was probably from a stale session.
            if (err.unauthorized || err.forbidden || err.notFound) {
                // We dispose of the session exactly like we do on logout.
                endSession();
                useUserSessionStore.setState({
                    profileLoadState: { phase: 'idle', userId: null, error: null },
                });
            } else {
                useUserSessionStore.setState({
                    profileLoadState: { phase: 'error', userId, error: err },
                });
            }
            onErr(err);
            finish(null);
        },
    );

    void loadPromise.then(() => {
        if (activeUserSessionLoad?.promise === loadPromise) {
            activeUserSessionLoad = null;
        }
    });

    return loadPromise;
}

/**
 * Loads a profile after any request already in flight has settled. Mutation
 * transitions use this to guarantee that account policy is read after the
 * backend has recomputed it rather than joining a pre-mutation request.
 */
export async function refreshUserSession(onErr: (err: ServerError) => void = noop): Promise<UserVo | null> {
    const inFlightLoad = activeUserSessionLoad?.promise;
    if (inFlightLoad) {
        await inFlightLoad;
    }

    return loadUserSession(onErr);
}

/** Retries an unavailable profile/policy load using the same in-flight dedupe. */
export function retryUserSession(onErr: (err: ServerError) => void = noop): Promise<UserVo | null> {
    return loadUserSession(onErr);
}

/**
 * Marks the current profile policy as stale, then guarantees a post-mutation
 * profile request. Responses started before invalidation are ignored.
 */
export function refreshUserSessionAfterPolicyChange(onErr: (err: ServerError) => void = noop): Promise<UserVo | null> {
    userProfileEpoch += 1;
    const userId = useAuthStore.getState().userId;
    useAuthStore.getState().actions.userInvalidated();
    if (userId) {
        useUserSessionStore.setState({
            loading: true,
            profileLoadState: { phase: 'loading', userId, error: null },
        });
    }
    return refreshUserSession(onErr);
}

export function handleLoginSuccess(
    type: LoginType,
    response: LoginResponse,
    provider?: OAuthProvider,
    onSuc: () => void = noop,
    onErr: () => void = noop,
    onTwoFactor: () => void = noop,
    onUnsupportedMobileSubUser: () => void = noop,
): void {
    // Handle the case when 2FA is required
    const authUser = useAuthStore.getState().user;
    if (authUser?.email) {
        const trackerPayload: FacebookPixelTrackerPayload = {
            page: window.location.pathname,
            event_id: uuidv4(),
        };
        // For Amplitude, this is replaced by recordSignUpMetric, but we still need it for GTM tags
        analyticsProvider.sendEvent('SignupProfileKycFailure', trackerPayload);
    }
    if (!!response.required_mfa_method && response.required_mfa_method !== 'none') {
        useTwoFactorAuthStore.getState().actions.setMfaStatus(response.required_mfa_method);
        if (response.required_mfa_method === 'authenticator-app') {
            // email login
            if (type === LoginType.EMAIL) {
                onTwoFactor();
                onErr();
            }
            // oAuth login
            else if (provider && !!response.mfa_required_token) {
                useTwoFactorAuthStore.getState().actions.setProvider(provider);
                useTwoFactorAuthStore.getState().actions.setMfaRequiredToken(response.mfa_required_token);
                onTwoFactor();
            }

            if (response.user_active === false) {
                useTwoFactorAuthStore.getState().actions.setAllowMFAReactivation(true);
            }
            return;
        }

        if (['sms', 'email', 'sms_and_email'].includes(response.required_mfa_method)) {
            // email login
            if (type === LoginType.EMAIL) {
                useTwoFactorAuthStore.getState().actions.setPhone(!!response.phone ? response.phone : '');
                useTwoFactorAuthStore.getState().actions.setMfaSentEmail(!!response.email ? response.email : '');
                onTwoFactor();
                onErr();
            }
            // oAuth login
            else if (provider && !!response.mfa_required_token) {
                useTwoFactorAuthStore.getState().actions.setProvider(provider);
                useTwoFactorAuthStore.getState().actions.setMfaRequiredToken(response.mfa_required_token);
                onTwoFactor();
            }

            if (response.user_active === false) {
                useTwoFactorAuthStore.getState().actions.setAllowReactivation(true);
            }
            return;
        }

        useSnackbarStore.getState().actions.pushMessage({
            text: 'Error logging in. User needs to configure 2FA.',
            variant: 'negative',
        });
        onErr();
        return;
    }

    setAccessLevel(response.access_level);
    setUserId(response.user_id);
    if (response.sub_user_id) {
        useAuthStore
            .getState()
            .actions.setSubUser(response.sub_user_id, response.effective_scopes ?? ['read', 'write']);
        if (isMobileWebDevice()) {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Team member accounts are not supported on mobile web.',
                variant: 'negative',
            });
            logout(noop, onUnsupportedMobileSubUser);
            return;
        }
    } else {
        useAuthStore.getState().actions.clearSubUser();
    }
    startFESession(response.user_id);
    void loadUserSession();

    // Flush attribution history on login (covers both email/password and OAuth flows)
    useRegistrationStore.getState().actions.sendAttributionHistory(response.user_id);

    if (!response.user_created && response.access_level !== 'applicant' && !response.sub_user_id) {
        void submitStoredReferralAfterLogin(response.user_id, getCountryCodeSnapshot);
    }

    onSuc();
}

export function handleLoginError(err: ServerError, email?: string, onExpired: () => void = noop): void {
    switch (err?.error?.code) {
        case 'user_not_kyc_verified': {
            if (email) {
                displayApplicationIncomplete(email);
            }
            break;
        }
        case 'expired_password': {
            onExpired();
            break;
        }
        case 'incorrect_mfa_code': {
            break;
        }
        case 'user_deactivated': {
            useSnackbarStore.getState().actions.pushMessage({
                text: getErrorMessage(err),
                variant: 'negative',
            });
            useTwoFactorAuthStore.getState().actions.setAllowReactivation(true);
            break;
        }
        default: {
            const neutral = err.maintenance || err.unavailable;
            useSnackbarStore.getState().actions.pushMessage({
                text: getErrorMessage(err),
                variant: neutral ? 'neutral' : 'negative',
            });
        }
    }
}

export async function twoFactorLogin(
    email: string,
    password: string,
    code: string,
    rememberDevice = false,
    reactivateUser = false,
    onSuc: () => void = noop,
    onErr: () => void = noop,
    onExpired: () => void = noop,
    onTwoFactor: () => void = noop,
    existingSardineSessionKey?: string,
    observedUserId?: string,
    onUnsupportedMobileSubUser: () => void = noop,
): Promise<void> {
    const sardineSessionKey = existingSardineSessionKey ?? (await fetchSardineSessionKey('login'));
    twoFactorLoginTransport(
        {
            email,
            password,
            code,
            remember_device: rememberDevice,
            reactivate_user: reactivateUser,
            sardine_session_key: sardineSessionKey || undefined,
            observed_user_id: observedUserId || undefined,
        },
        (response) => {
            if (!getUserPropertiesSnapshot().createdCredentials) {
                updateUserProperties({ createdCredentials: true }, response.user_id);
            }
            handleLoginSuccess(
                LoginType.EMAIL,
                response,
                undefined,
                onSuc,
                onErr,
                onTwoFactor,
                onUnsupportedMobileSubUser,
            );
        },
        (err) => {
            handleLoginError(err, email, onExpired);
            onErr();
        },
    );
}

export async function oAuthLogin(
    provider: OAuthProvider,
    oAuthCode: string,
    redirectUri?: string,
    onSuc: () => void = noop,
    onErr: () => void = noop,
    onTwoFactor: () => void = noop,
    onUnsupportedMobileSubUser: () => void = noop,
): Promise<void> {
    const utmData = useAttributionStore.getState().utmData;
    const { referral, invalidatedReferralCode } = useReferralStore.getState();
    const referralCode = resolveEligibleReferralForSubmission(
        referral,
        useAuthStore.getState().user?.country || getCountryCodeSnapshot(),
        invalidatedReferralCode,
    );
    const sardineSessionKey = await fetchSardineSessionKey('login');
    oauthLogin(
        provider,
        {
            oauth_code: oAuthCode,
            redirect_uri: redirectUri,
            utm_campaign: utmData.utm_campaign,
            utm_source: utmData.utm_source,
            utm_medium: utmData.utm_medium,
            utm_term: utmData.utm_term,
            utm_content: utmData.utm_content,
            utm_landing: utmData.utm_landing,
            referral_code: referralCode,
            sardine_session_key: sardineSessionKey || undefined,
        },
        (response) => {
            handleLoginSuccess(
                LoginType.OAUTH,
                response,
                provider,
                () => {
                    handleOAuthLoginSuccess(response, provider);
                    onSuc();
                },
                onErr,
                onTwoFactor,
                onUnsupportedMobileSubUser,
            );
        },
        (err) => {
            handleLoginError(err);
            onErr();
        },
    );
}

export async function oAuthTwoFactorLogin(
    provider: OAuthProvider,
    mfaRequiredToken: string,
    mfaCode: string,
    rememberDevice = false,
    onSuc: () => void = noop,
    onErr: () => void = noop,
    onUnsupportedMobileSubUser: () => void = noop,
): Promise<void> {
    const sardineSessionKey = await fetchSardineSessionKey('login');
    oauthTwoFactorLogin(
        {
            mfa_required_token: mfaRequiredToken,
            mfa_code: mfaCode,
            remember_device: rememberDevice,
            sardine_session_key: sardineSessionKey || undefined,
        },
        (response) => {
            handleLoginSuccess(
                LoginType.OAUTH,
                response,
                provider,
                () => {
                    handleOAuthLoginSuccess(response, provider);
                    onSuc();
                },
                onErr,
                noop,
                onUnsupportedMobileSubUser,
            );
        },
        (err) => {
            handleLoginError(err);
            onErr();
        },
    );
}

export function handleOAuthLoginSuccess(response: LoginResponse, provider: OAuthProvider): void {
    if (provider === OAuthProvider.GOOGLE) {
        if (response.given_name) {
            setLocalStorageWithExpiration(OAUTH_PREFILL_FIRST_NAME, response.given_name);
        }
        if (response.family_name) {
            setLocalStorageWithExpiration(OAUTH_PREFILL_LAST_NAME, response.family_name);
        }
    }

    // Fire only when this OAuth call actually created the account. The previous
    // !alreadySignUpWithOauth guard hydrated from the server async after this check,
    // so returning OAuth logins re-fired SignupProfile -> duplicate Registration conversions.
    if (response.user_created) {
        const trackerPayload: FacebookPixelTrackerPayload = {
            page: window.location.pathname,
            event_id: response.user_id ? growthEventId(response.user_id, GrowthEvent.Signup) : uuidv4(),
        };
        // Push the real external_id before SignupProfile so the Addressable registration
        // conversion has identity; setUserId (which also pushes it) runs after this event.
        if (typeof window !== 'undefined' && response.user_id) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ external_id: response.user_id, is_registered_user: true });
        }
        // For Amplitude, this is replaced by recordSignUpMetric, but we still need it for GTM tags
        analyticsProvider.sendEvent('SignupProfile', trackerPayload);
        // response.user_id override: this runs before setUserId seeds the authStore.
        updateUserProperties({ alreadySignUpWithOauth: true }, response.user_id);
    }

    setUserId(response.user_id);
    if (typeof window !== 'undefined' && window.location.hostname === 'alpha.kalshi.com') {
        setSprigAttribute('logged_in_alpha', true);
    }
}

export function logout(onSuc: () => void = noop, onFinally: () => void = noop): void {
    //The FE session must be cleared before the authentication to the backend is removed. Even a 500 to this endpoint doesn't stop from signing out.
    const feSessionID = getValidFrontendSessionId();
    !!feSessionID
        ? endFESession(
              useAuthStore.getState().userId ?? '',
              feSessionID,
              () => {
                  clearSessionID();
              },
              noop,
              () => {
                  logoutTransport(
                      () => {
                          endSession();
                          onSuc();
                      },
                      noop,
                      onFinally,
                  );
              },
          )
        : logoutTransport(
              () => {
                  endSession();
                  onSuc();
              },
              noop,
              onFinally,
          );
}

export function deactivateAccount(
    onSuc: () => void = noop,
    onError: () => void = noop,
    onFinally: () => void = noop,
): void {
    const isLoggedIn = useAuthStore.getState().isLoggedIn;
    const userId = useAuthStore.getState().userId ?? '';
    if (!isLoggedIn) {
        onFinally();
        return;
    }

    //The FE session must be cleared before the authentication to the backend is removed. Even a 500 to this endpoint doesn't stop from signing out.
    const feSessionID = getValidFrontendSessionId();
    !!feSessionID
        ? endFESession(
              useAuthStore.getState().userId ?? '',
              feSessionID,
              () => {
                  clearSessionID();
              },
              noop,
              () => {
                  deactivateAccountTransport(
                      userId,
                      () => {
                          endSession();
                          onSuc();
                      },
                      onError,
                      onFinally,
                  );
              },
          )
        : deactivateAccountTransport(
              userId,
              () => {
                  endSession();
                  onSuc();
              },
              onError,
              onFinally,
          );
}

export function endSession(): void {
    resetSardineState();
    clearUserId();
    clearAccessLevel();
    runAppLogoutTeardown();
    resetUserOrdersRefreshState();
    clearNotificationFlushPending();
}

// Invoked by the forced-disconnect callback appEffects registers with
// lib/stores/sessionTeardownEffects.ts (session_expired / maintenance /
// unavailability) — TransportLayer sets the zustand logout state; this only
// handles the residual teardown (it does not itself touch authStore, and the
// storage cleanup lives in sessionTeardownEffects.ts).
export function applyForcedDisconnectTeardown(): void {
    // Drop any debounced notification-pref save before we tear down the
    // session — otherwise a click within the debounce window would
    // PATCH /users//subscribe with a blank userId after disconnect.
    // endSession() handles the explicit logout path; this is the
    // session_expired / maintenance / forced-disconnect path.
    clearNotificationFlushPending();
    runAppLogoutTeardown();

    resetUserOrdersRefreshState();
}

export function clearSessionID(): void {
    clearFrontendSessionId();
}

// This is the official place where we identify the logged users
export function setUserId(val: string): void {
    setLocalStorageWithExpiration(USER_ID_LOCAL_STORAGE_KEY, val);
    setCookieWithExpiration(USER_ID_LOCAL_STORAGE_KEY, val);
    useAuthStore.getState().actions.login(val);
    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ external_id: val, is_registered_user: true });
    }

    // Keep Zustand authStore in sync so components reading userId from
    // the hook (e.g. GoogleOneTapScript) see the update without a reload.
    useAuthStore.getState().actions._initializeFromCookie();
}

// This is the official place where we dispose the current userId
export function clearUserId(): void {
    localStorage.removeItem(USER_ID_LOCAL_STORAGE_KEY);
    localStorage.removeItem(EMAIL_LOCAL_STORAGE_KEY);
    localStorage.removeItem(PHONE_LOCAL_STORAGE_KEY);
    removeCookie(USER_ID_LOCAL_STORAGE_KEY);
    useAuthStore.getState().actions.logout('manual');
    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ is_registered_user: false });
    }
    clearCsrfToken();
    useAuthStore.getState().actions._initializeFromCookie();
}

export function clearAccessLevel(): void {
    localStorage.removeItem(USER_ROLE_LOCAL_STORAGE_KEY);
    useAuthStore.getState().actions.clearAccessLevel();
}

export function setAccessLevel(val: AccessLevel): void {
    setLocalStorageWithExpiration(USER_ROLE_LOCAL_STORAGE_KEY, val);
    useAuthStore.getState().actions.setAccessLevel(val);
}

export function startFESession(userId: string): void {
    startFESessionTransport(userId, (response) => {
        storeFrontendSessionId(response.session_id);
    });
}

function updateUserInfoAndProfile(userInfo: UpdateUserRequest, onSuc: () => void, onErr: () => void = noop): void {
    const userId = useAuthStore.getState().userId;
    if (!userId) {
        return;
    }

    const user = useAuthStore.getState().user;
    if (!user) {
        return;
    }

    updateUserInfo(
        userId,
        userInfo,
        () => {
            // Address changes can change the backend-computed perps-only policy.
            // Wait for the replacement policy when available, but a transient
            // refresh failure must not block a still-authenticated user.
            void refreshUserSessionAfterPolicyChange(handleSnackbarError).then(() => {
                if (useAuthStore.getState().userId === userId) onSuc();
                else onErr();
            });
        },
        (err) => {
            handleSnackbarError(err);
            onErr();
        },
    );
}

export async function updateUserAddress(): Promise<void> {
    const user = useAuthStore.getState().user;
    if (!user) {
        return;
    }

    // Awaited rather than peeked: peek returns null until startSardineFlow's session
    // request lands, which would silently drop the key on a fast submit. fetch waits
    // for the in-flight session and mints one only if there isn't one.
    const sardineSessionKey = await fetchSardineSessionKey('profile_change', useAuthStore.getState().userId ?? '');

    const registrationState = useRegistrationStore.getState();
    updateUserInfoAndProfile(
        {
            first_name: user.first_name,
            last_name: user.last_name,
            city: registrationState.city,
            state: registrationState.state,
            country: registrationState.country,
            postal_code: registrationState.postalCode,
            street1: registrationState.address1,
            street2: registrationState.address2,
            birth_date: user.birth_date,
            use_bid_ask: user.use_bid_ask,
            sardine_session_key: sardineSessionKey || undefined,
        },
        (): void => {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Successfully updated address',
                variant: 'positive',
            });
        },
    );
}

export function requestUpdateUserEmail(
    email: string,
    onSuc: () => void = noop,
    onErr: (error: ServerError) => void = noop,
): void {
    requestUpdateUserEmailTransport(useAuthStore.getState().userId ?? '', { email: email }, onSuc, onErr);
}

export async function updateUserEmail(
    email: string,
    mfaCode: string,
    onSuc: () => void = noop,
    onErr: (error: ServerError) => void = noop,
): Promise<void> {
    const sardineSessionKey = await fetchSardineSessionKey('email_change', useAuthStore.getState().userId ?? '');
    updateUserEmailTransport(
        useAuthStore.getState().userId ?? '',
        { email: email, mfa_code: mfaCode, sardine_session_key: sardineSessionKey || undefined },
        () => {
            onSuc();
            useSnackbarStore.getState().actions.pushMessage({
                text: `Confirmation email sent to ${email}`,
                variant: 'positive',
            });
        },
        onErr,
    );
}

export function verifyPendingEmail(code: string, email: string): void {
    const userId = useAuthStore.getState().userId ?? '';
    verifyPendingEmailTransport(
        userId,
        { code: code, email: email },
        () => {
            useSnackbarStore.getState().actions.pushMessage({
                text: `Email updated!`,
                variant: 'positive',
            });
        },
        handleSnackbarError,
    );
}

/** Reactive, formatted phone number — derives from authStore's user profile. */
export function usePhoneNumberWithSeparators(): string {
    const authUser = useAuthStore((s) => s.user);
    try {
        const fullNumber = `+${authUser?.country_code ?? ''}${authUser?.area_code ?? ''}${authUser?.phone_number ?? ''}`;
        const parsed = parsePhoneNumberWithError(fullNumber);
        return parsed.formatInternational();
    } catch {
        return `+${authUser?.country_code ?? ''} ${authUser?.area_code ?? ''} ${authUser?.phone_number ?? ''}`;
    }
}

export function verifyAccount(
    userId: string,
    code: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
): CancelTokenSource | null {
    const trimmedCode = code.trim();

    if (!useUserSessionStore.getState().verifying) {
        useUserSessionStore.setState({ verifying: true });
        return verifyAccountTransport(
            userId,
            { code: trimmedCode },
            () => {
                onSuc();
            },
            onErr,
            () => {
                useUserSessionStore.setState({ verifying: false });
            },
        );
    }

    return null;
}

export function resendVerification(userId: string, onSuc: () => void = noop, noPushMessage?: boolean): void {
    resendAccountVerification(
        userId,
        () => {
            if (!noPushMessage) {
                useSnackbarStore.getState().actions.pushMessage({
                    text: `We generated a new verification code for you. Check your e-mail in a minute!`,
                    variant: 'positive',
                });
            }

            onSuc();
        },
        handleSnackbarError,
    );
}

export function createMarketRequest(
    payload: CreateMarketRequestRequest,
    onSuc: () => void = noop,
    onErr: () => void = noop,
): void {
    createMarketRequestTransport(
        useAuthStore.getState().userId ?? '',
        payload,
        () => {
            onSuc();
        },
        (err) => {
            handleSnackbarError(err);
            onErr();
        },
    );
}

export function sendPasswordReset(
    email: string,
    onSuc: () => void,
    onFinally: () => void,
    onDeactivatedUser: () => void,
): void {
    sendResetPasswordLink(
        email,
        () => {
            onSuc();
        },
        (err) => {
            if (err.error.code === 'user_deactivated') {
                onDeactivatedUser();
            }
        },
        onFinally,
    );
}

export function finishPasswordReset(
    userId: string,
    code: string,
    password: string,
    onSuc: () => void,
    onFinally: () => void,
): void {
    verifyPasswordReset(
        userId,
        code,
        password,
        () => {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Your password was reset successfully.',
                variant: 'positive',
            });
            onSuc();
        },
        (err) => {
            if (err.error.data.code === 'not_found' || err.error.data.code === 'invalid_or_expired_code') {
                useSnackbarStore.getState().actions.pushMessage({
                    text: "Your email code is expired or invalid. Please go back and click 'Forgot Your Password' again to request a new one",
                    variant: 'negative',
                });
            } else {
                handleSnackbarError(err);
            }
        },
        onFinally,
    );
}

/**
 * Resets the session's local bookkeeping (order-WS subscription, loading/
 * verifying flags) without touching authStore. Called both from the app logout
 * teardown (`resetAppStateOnLogout`) and from a not-logged-in signup-modal close
 * (`disposeWithoutExternalProducts` in the old UserStore — the two were
 * byte-for-byte identical bodies, so this single function now covers both).
 */
export function resetUserSession(): void {
    userProfileEpoch += 1;
    activeUserSessionLoad = null;
    unsubscribeUserOrdersWs();
    useUserSessionStore.setState({
        loading: false,
        verifying: false,
        profileLoadState: { phase: 'idle', userId: null, error: null },
    });
}
